import React, { useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import NiceModal from "@ebay/nice-modal-react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Button } from "components/Button";
import { useTranslate } from "hooks";
import type { ProductOutput, SelectedAccessory } from "stores/product";
import {
  getAccessoryFromGroup,
  useProductActions,
  useProductOutput,
} from "stores/product";
import { ProductAccessoriesModalPart } from "./ProductAccessoriesModalPart";

export const ProductAccessoriesModal = NiceModal.create(
  Scrivito.connect(() => {
    const translate = useTranslate();
    const modal = NiceModal.useModal();
    const actions = useProductActions();
    const productOutput = useProductOutput();
    const product = productOutput?.product;
    const accessoryGroups = product?.accessoryGroups;
    const [hasProductChanged, setHasProductChanged] = useState(false);

    const [selectedAccessories, setSelectedAccessories] = useState<
      SelectedAccessory[]
    >(initAccessorySelection(productOutput));

    const handleSelectAccessory = (
      presentation: string,
      accessoryGroupId?: string | null,
    ) => {
      if (!accessoryGroupId) {
        return;
      }
      const accessory = getAccessoryFromGroup(presentation, accessoryGroupId);
      if (!accessory) {
        return;
      }

      setSelectedAccessories((prev) => {
        const filteredAccessories = prev.filter(
          (accessory) => accessory?.accessoryGroupId !== accessoryGroupId,
        );
        return [...filteredAccessories, accessory];
      });
      setHasProductChanged(true);
    };

    const handleUpdateConfiguration = useCallback(() => {
      actions.selectAccessories(selectedAccessories);
      modal.remove();
    }, [actions, modal, selectedAccessories]);

    return (
      <Modal
        isOpen={modal.visible}
        centered={true}
        size="lg"
        toggle={modal.remove}
        scrollable={true}
        style={{ zIndex: 9999 }}
      >
        <ModalHeader
          toggle={modal.remove}
          tag="div"
          className="d-flex align-items-start pb-0"
        >
          <h3 className="text-uppercase lh-32">
            {translate("PRODUCT_CONFIGURATOR_ACCESSORIES")}
          </h3>
        </ModalHeader>
        <ModalBody className="d-flex flex-column gap-4 mb-5">
          {accessoryGroups?.map((accessoryGroup) => (
            <ProductAccessoriesModalPart
              accessoryGroup={accessoryGroup}
              selectedAccessories={selectedAccessories}
              key={`group-${accessoryGroup?.id}`}
              onSelectionChange={(presentation) =>
                handleSelectAccessory(presentation, accessoryGroup?.id)
              }
            />
          ))}
        </ModalBody>
        <ModalFooter className="pt-0 d-flex gap-2">
          <Button
            data-testid="product-adding-modal-cancel-btn"
            disabled={false}
            isLoading={false}
            className="m-0 btn-tertiary"
            onClick={modal.remove}
          >
            {translate("CANCEL")}
          </Button>
          <Button
            data-testid="product-adding-modal-save-btn"
            className="m-0"
            onClick={handleUpdateConfiguration}
            disabled={!hasProductChanged}
          >
            {translate("PRODUCT_CONFIGURATOR_UPDATE_CONFIGURATION")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }),
);

const initAccessorySelection = (productOutput: ProductOutput | null) =>
  productOutput?.product?.selectedAccessories
    ? productOutput?.product?.selectedAccessories.filter(
        (accessory): accessory is SelectedAccessory =>
          Boolean(accessory?.accessoryId && accessory.accessoryGroupId),
      )
    : [];
